/**
 * Base 44 Numeric Engine
 * Maps letters (A-Z) to numeric values (1-44) using a custom frequency scale
 * Includes vibrational reduction for sums > 44
 */

// Custom Base 44 mapping: A=1, B=2, ..., Z=26, then special symbols 27-44
const BASE44_MAP: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, I: 9, J: 10,
  K: 11, L: 12, M: 13, N: 14, O: 15, P: 16, Q: 17, R: 18, S: 19, T: 20,
  U: 21, V: 22, W: 23, X: 24, Y: 25, Z: 26,
};

/**
 * Calculate the numeric value of a single character
 */
export function charToBase44(char: string): number {
  return BASE44_MAP[char.toUpperCase()] || 0;
}

/**
 * Calculate the raw sum of a word in Base 44
 */
export function calculateRawSum(input: string): number {
  return input
    .split('')
    .reduce((sum, char) => sum + charToBase44(char), 0);
}

/**
 * Vibrational Reduction: Recursively reduce sum until <= 44
 * This is the "Core Frequency" - the essence of the word
 */
export function vibrationalReduction(sum: number): number {
  if (sum <= 44) return sum;
  
  // Recursive addition of digits
  const digitSum = sum
    .toString()
    .split('')
    .reduce((acc, digit) => acc + parseInt(digit, 10), 0);
  
  return vibrationalReduction(digitSum);
}

/**
 * Calculate the Base 44 Core Frequency for a given input
 */
export function calculateBase44Frequency(input: string): {
  rawSum: number;
  coreFrequency: number;
  vibrationalPath: number[];
} {
  const rawSum = calculateRawSum(input);
  const path: number[] = [rawSum];
  
  let current = rawSum;
  while (current > 44) {
    current = vibrationalReduction(current);
    path.push(current);
  }
  
  return {
    rawSum,
    coreFrequency: current,
    vibrationalPath: path,
  };
}

/**
 * Calculate divergence between two frequencies
 * High divergence = significant linguistic drift
 */
export function calculateDivergence(ancient: number, modern: number): {
  divergence: number;
  percentage: number;
  severity: 'minimal' | 'moderate' | 'severe';
} {
  const divergence = Math.abs(modern - ancient);
  const percentage = (divergence / 44) * 100;
  
  let severity: 'minimal' | 'moderate' | 'severe';
  if (percentage < 25) severity = 'minimal';
  else if (percentage < 50) severity = 'moderate';
  else severity = 'severe';
  
  return {
    divergence,
    percentage: Math.round(percentage),
    severity,
  };
}
